/**
 * IncidentForm Component - Form for creating/editing incidents
 * Epic 6: Quality/Incidents/Exceptions
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { format } from "date-fns";

const INCIDENT_TYPES = [
  "LOST_ITEM",
  "DAMAGE",
  "PARTIAL_DELIVERY",
  "LATE_ARRIVAL",
  "LATE_DELIVERY",
  "WRONG_VEHICLE",
  "WRONG_CREW",
  "CUSTOMER_COMPLAINT",
  "PROVIDER_MISCONDUCT",
  "SERVICE_NOT_RENDERED",
  "NO_SHOW",
  "FALSE_CLAIM",
  "OTHER",
];

const SEVERITY_LEVELS = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

const ACTOR_TYPES = ["OPS", "DRIVER", "CUSTOMER"];

/**
 * IncidentForm Component
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSubmit - Submit handler
 * @param {Object} [props.initialData] - Initial form data
 */
const IncidentForm = ({ open, onClose, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState({
    type: initialData?.type || "",
    severity: initialData?.severity || "MEDIUM",
    description: initialData?.description || "",
    occurredAt: initialData?.occurredAt
      ? format(new Date(initialData.occurredAt), "yyyy-MM-dd'T'HH:mm")
      : format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    reportedBy: {
      actorType: initialData?.reportedBy?.actorType || "OPS",
      actorId: initialData?.reportedBy?.actorId || "",
    },
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = "Incident type is required";
    }
    if (!formData.severity) {
      newErrors.severity = "Severity is required";
    }
    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }
    if (!formData.occurredAt) {
      newErrors.occurredAt = "Occurrence date/time is required";
    }
    if (!formData.reportedBy.actorId) {
      newErrors["reportedBy.actorId"] = "Reporter ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        occurredAt: new Date(formData.occurredAt).toISOString(),
      };
      await onSubmit(submitData);
      handleClose();
    } catch (error) {
      console.error("Error submitting incident:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      type: "",
      severity: "MEDIUM",
      description: "",
      occurredAt: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      reportedBy: {
        actorType: "OPS",
        actorId: "",
      },
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Incident" : "Create New Incident"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            select
            label="Incident Type"
            value={formData.type}
            onChange={(e) => handleChange("type", e.target.value)}
            fullWidth
            required
            error={!!errors.type}
            helperText={errors.type}
          >
            {INCIDENT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Severity"
            value={formData.severity}
            onChange={(e) => handleChange("severity", e.target.value)}
            fullWidth
            required
            error={!!errors.severity}
            helperText={errors.severity}
          >
            {SEVERITY_LEVELS.map((severity) => (
              <MenuItem key={severity} value={severity}>
                {severity}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            fullWidth
            required
            multiline
            rows={4}
            error={!!errors.description}
            helperText={errors.description || "Provide a detailed description of the incident"}
          />

          <TextField
            label="Occurred At"
            type="datetime-local"
            value={formData.occurredAt}
            onChange={(e) => handleChange("occurredAt", e.target.value)}
            fullWidth
            required
            InputLabelProps={{
              shrink: true,
            }}
            error={!!errors.occurredAt}
            helperText={errors.occurredAt}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              select
              label="Reported By"
              value={formData.reportedBy.actorType}
              onChange={(e) =>
                handleChange("reportedBy.actorType", e.target.value)
              }
              sx={{ flex: 1 }}
            >
              {ACTOR_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Reporter ID"
              value={formData.reportedBy.actorId}
              onChange={(e) =>
                handleChange("reportedBy.actorId", e.target.value)
              }
              sx={{ flex: 1 }}
              required
              error={!!errors["reportedBy.actorId"]}
              helperText={errors["reportedBy.actorId"]}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {submitting ? "Creating..." : initialData ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncidentForm;
